// Entrée MIDI (USB ou Bluetooth) via la Web MIDI API.
// Disponible dans Chrome, Edge et Opera (bureau et Android) et dans Firefox (avec
// une demande de permission). Absente de Safari, donc d'iPad et d'iPhone.

import { BaseInputSource, now } from './types';

export interface ParsedMidiMessage {
  type: 'noteOn' | 'noteOff' | 'other';
  channel: number;
  pitch: number;
  velocity: number;
}

/** Analyse un message MIDI brut. Un « note on » de vélocité 0 vaut « note off ». */
export function parseMidiMessage(data: ArrayLike<number>): ParsedMidiMessage {
  if (data.length === 0) return { type: 'other', channel: 0, pitch: 0, velocity: 0 };
  const status = data[0] & 0xf0;
  const channel = data[0] & 0x0f;
  const pitch = data.length > 1 ? data[1] & 0x7f : 0;
  const velocity = data.length > 2 ? data[2] & 0x7f : 0;
  if (status === 0x90) return { type: velocity > 0 ? 'noteOn' : 'noteOff', channel, pitch, velocity };
  if (status === 0x80) return { type: 'noteOff', channel, pitch, velocity };
  return { type: 'other', channel, pitch, velocity };
}

export class MidiInputSource extends BaseInputSource {
  private access: MIDIAccess | null = null;
  private readonly attached = new Map<string, MIDIInput>();
  private preferredId: string | null;
  private lostSince: number | null = null;

  constructor(options: { deviceId?: string | null } = {}) {
    super('midi');
    this.preferredId = options.deviceId ?? null;
  }

  static isSupported(): boolean {
    return typeof navigator !== 'undefined' && typeof navigator.requestMIDIAccess === 'function';
  }

  async start(): Promise<void> {
    if (!MidiInputSource.isSupported()) {
      const insecure = typeof window !== 'undefined' && window.isSecureContext === false;
      this.setStatus({
        state: 'unsupported',
        code: insecure ? 'insecure' : 'unsupported',
        message: insecure
          ? 'La page n’est pas ouverte en HTTPS : le navigateur cache le MIDI. Ouvrez l’adresse qui commence par https://.'
          : 'Ce navigateur ne propose pas le MIDI (Web MIDI). Utilisez Chrome, Edge ou Firefox, ou passez au micro.',
      });
      return;
    }
    this.setStatus({ state: 'starting', code: undefined, message: 'Demande d’accès MIDI…' });
    try {
      this.access = await navigator.requestMIDIAccess({ sysex: false });
    } catch (err) {
      const e = err as { name?: string; message?: string };
      const denied = e?.name === 'SecurityError' || e?.name === 'NotAllowedError';
      this.setStatus({ state: 'error', code: denied ? 'denied' : 'error', message: denied ? 'Accès MIDI refusé. Autorisez les périphériques MIDI pour ce site, puis réessayez.' : `Accès MIDI indisponible (${e?.message ?? 'erreur'}).` });
      return;
    }
    this.access.onstatechange = (ev: Event) => this.handleStateChange(ev as MIDIConnectionEvent);
    this.refresh();
  }

  stop(): void {
    for (const input of this.attached.values()) input.onmidimessage = null;
    this.attached.clear();
    if (this.access) this.access.onstatechange = null;
    this.access = null;
    this.setStatus({ state: 'idle', code: undefined, message: '', devices: [], activeDevice: undefined });
  }

  selectDevice(id: string | null): void {
    this.preferredId = id;
    this.refresh();
  }

  private refresh(): void {
    if (!this.access) return;
    const inputs = [...this.access.inputs.values()];
    const devices = inputs.map((i) => ({ id: i.id, name: i.name || 'Périphérique MIDI' }));
    const wanted = inputs.filter((i) => this.preferredId === null || i.id === this.preferredId);
    for (const input of wanted) {
      if (!this.attached.has(input.id)) this.attach(input);
    }
    for (const [id, input] of this.attached) {
      if (!wanted.some((w) => w.id === id)) {
        input.onmidimessage = null;
        this.attached.delete(id);
      }
    }
    const names = [...this.attached.values()].map((i) => i.name || 'Périphérique MIDI');
    if (this.attached.size > 0) {
      this.setStatus({ state: 'active', code: undefined, message: `MIDI connecté : ${names.join(', ')}`, devices, activeDevice: names[0] });
    } else if (devices.length > 0) {
      this.setStatus({ state: 'active', code: 'noDevice', message: 'Périphérique MIDI non sélectionné.', devices, activeDevice: undefined });
    } else {
      this.setStatus({ state: 'active', code: 'noDevice', message: 'Aucun piano MIDI détecté. Branchez le câble USB (prise « USB to Host ») et allumez le piano.', devices, activeDevice: undefined });
    }
  }

  private attach(input: MIDIInput): void {
    this.attached.set(input.id, input);
    input.onmidimessage = (e: MIDIMessageEvent) => {
      if (!e.data) return;
      const parsed = parseMidiMessage(e.data);
      const time = e.timeStamp > 0 ? e.timeStamp : now();
      if (parsed.type === 'noteOn') {
        this.emit({ type: 'noteOn', pitch: parsed.pitch, time, velocity: parsed.velocity / 127, confidence: 'sure', source: 'midi' });
      } else if (parsed.type === 'noteOff') {
        this.emit({ type: 'noteOff', pitch: parsed.pitch, time, source: 'midi' });
      }
    };
  }

  private handleStateChange(ev: MIDIConnectionEvent): void {
    const port = ev.port;
    if (!port || port.type !== 'input') return;
    const hadDevices = this.attached.size > 0;
    this.refresh();
    const hasDevices = this.attached.size > 0;
    if (hadDevices && !hasDevices) {
      this.lostSince = now();
      this.setStatus({ state: 'lost', code: 'lost', message: `Connexion MIDI perdue (${port.name || 'périphérique'} débranché).` });
      this.emit({ type: 'connection', state: 'lost', time: this.lostSince, source: 'midi', detail: 'piano MIDI débranché' });
    } else if (!hadDevices && hasDevices && this.lostSince !== null) {
      this.lostSince = null;
      this.emit({ type: 'connection', state: 'restored', time: now(), source: 'midi', detail: 'piano MIDI rebranché' });
    }
  }
}
